import { ActionDto, ImageTypeDto, PreviousChat } from 'client/types';
import { getCookieValue } from './cookies';
import initParams from 'store/initParams';
import { SofiaVoiceMode } from 'types';
import { getQueryParam } from './shared';

/**
 * Determina si la aplicacion es nativa y corre en iOS
 */
export const isIosApp = () => {
  return /(?=.*Mobile D!)(?=.*Apple)/i.test(window.navigator.userAgent);
};

/**
 * Determina si el dispositivo es un dispositivo de Apple
 */
export const isAppleDevice = () => {
  return /\b(Macintosh|MacIntel|MacPPC|Mac68K|iPhone|iPad|iPod)\b/i.test(window.navigator.userAgent) || isIosApp();
};

/**
 * Determina si el navegador es Safari
 */
export const isSafariBrowser = () => {
  return /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
};

export const isBlank = (str: string) => typeof str === 'undefined' || str === null || str == 'null' || str == 'undefined' || str.trim() === '';

/**
 * Aplica el linkOffDomain a una URL si es que esta no es un dominio valido
 */
export const applyLinkOffDomain = (url: string): string => {
  const hasLinkOffDomain = typeof initParams.linkOffDomain !== 'undefined' && initParams.linkOffDomain.trim() !== '';
  const hasDomain = url.startsWith('http://') || url.startsWith('https://');

  // If is a valid URL or no linkOffDomain is set, return the URL as is
  if (hasDomain || !hasLinkOffDomain) {
    return url;
  } else {
    return `${initParams.linkOffDomain}${url.startsWith('/') ? '' : '/'}${url}`;
  }
};

/**
 * Will try to open the url in a new tab. If it fails then it will override the current window.locaction with the given url.
 * @param url
 */
export const openInNewTab = (url: string) => {
  const linkOffUrl = applyLinkOffDomain(url);

  console.debug('Try opening new tab with url: ', linkOffUrl);

  var newTab: Window | null = window.open(linkOffUrl, '_blank');
  if (newTab) {
    // for security reasons
    newTab.opener = null;
  } else {
    console.warn('Could not open in new tab, replacing current location');
    window.document.location.href = linkOffUrl;
  }
};

export const createNewTab = (isApp: boolean) => {
  const openSameWindow = url => (window.document.location.href = applyLinkOffDomain(url));
  const openTabBlank = (url, newTab) => {
    if (newTab === null) {
      openSameWindow(url);
    } else {
      newTab.location.href = applyLinkOffDomain(url);
    }
  };

  if (isApp) {
    return {
      open: url => openSameWindow(url),
      close: () => {},
    };
  } else {
    const newTab: Window | null = window.open('/chappie/views/loading', '_blank');
    if (!!newTab) {
      newTab.opener = null;
    }

    return {
      open: url => openTabBlank(url, newTab),
      close: () => newTab?.close(),
    };
  }
};

/**
 * Valida si el usuario tiene soporte para una feature en particular de la aplicacion
 * @param featureToFind
 */
export const hasSupportFeature = (featureToFind: 'mic' | 'reels' | 'location') => {
  const xDespFeature = getCookieValue('xdesp-AppFeatures');
  return typeof xDespFeature === 'string' && xDespFeature.split('|').some(feature => feature === featureToFind);
};

/**
 * Copia un texto al portapapeles
 * @param text
 */
export const copyToClipboard = (text: string): Promise<void> => {
  return navigator.clipboard.writeText(text);
};

/**
 * Retrasa la ejecucion de una funcion a un timeout para evitar multiples llamadas
 * @param func
 * @param wait
 * @param immediate
 */
export function debounce<T extends (...args: any[]) => void>(func: T, wait: number, immediate: boolean = false): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };

    const callNow = immediate && !timeout;
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(context, args);
    }
  };
}

/**
 * Registra un mutation observer sobre un elemento para monitorear si se agrega un elemento con una clase determinada y ejecutar un callback
 * @param node
 * @param classToFind
 * @param callback
 */
export function bindMutationObserver(node: HTMLElement & Node, classToFind: string, callback: () => void) {
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement && node.classList.contains(classToFind)) {
            callback();
          }
        });
      }
    }
  });

  observer.observe(node, { childList: true, subtree: true });

  return observer;
}

/**
 * Obtiene el chatId y el userId de los query params (si es que existen)
 */
export function getPreviousChat(): PreviousChat | null {
  const queryParams = new URLSearchParams(window.location.search);
  const loadChatParams = queryParams.has('cid') && queryParams.has('uid');
  const loadChatSessionStorage = typeof sessionStorage?.getItem === 'function' && sessionStorage?.getItem('chatId') !== null && sessionStorage?.getItem('userId') !== null;

  if (loadChatParams) {
    return {
      chatId: queryParams.get('cid') as string,
      userId: queryParams.get('uid') as string,
      mode: 'QS',
    };
  } else if (loadChatSessionStorage) {
    return {
      chatId: sessionStorage.getItem('chatId') as string,
      userId: sessionStorage.getItem('userId') as string,
      mode: 'QS',
    };
  } else {
    return null;
  }
}

/**
 * Retorna el ref de los query params
 */
export function getRefChatQs(): string {
  return getQueryParam('ref') ?? 'noref';
}

/**
 * Retorna la cantidad aproximada de caracteres que entran en la cantidad de lineas especificadas segun el tamaño del container
 * @param containerSize
 * @param averageCharWidth
 * @param lines
 */
export function calculateTextLength(containerSize: number, averageCharWidth: number, lines: number): number {
  return (containerSize / averageCharWidth) * lines;
}

/**
 * Retorna un objeto con la cantidad de adultos, menores y edades a partir de una distribucion
 * @param distributionStr
 */
export function extractDistributionObjFromStr(distributionStr: string): { adults: number; minors: number; ageMinors: string[] } {
  const parts = distributionStr.split('-').map(Number);
  const adults = parts[0];
  const ageMinors = parts.slice(1);
  const minors = ageMinors.length;

  return {
    adults: adults,
    minors: minors,
    ageMinors: ageMinors.map(age => age.toString(10)),
  };
}

/**
 * Returns a string with the distribution of adults and minors, including their ages.
 * @param adults - The number of adults.
 * @param ageMinors - An array of strings representing the ages of minors.
 * @returns A formatted string in the format "adults-age1-age2-...".
 */
export function parseDistributionObjToStr(adults: number, ageMinors: string[]): string {
  const minorsDistribution = ageMinors.length > 0 ? `-${ageMinors.join('-')}` : '';
  return `${adults}${minorsDistribution}`;
}

/**
 * Returns an updated version of an action with the distribution of adults and minors, including their ages.
 * @param adults - The number of adults.
 * @param ageMinors - An array of strings representing the ages of minors.
 * @param action - The action to update.
 * @returns The updated action.
 */
export function updateActionDistribution(adults: number, ageMinors: string[], action?: ActionDto): ActionDto | undefined {
  if (typeof action !== 'undefined') {
    return {
      ...action,
      params:
        typeof action.params !== 'undefined'
          ? JSON.stringify({
              ...JSON.parse(action.params),
              adults: adults + ageMinors.filter(age => Number(age) > 11).length,
              children: ageMinors.filter(age => Number(age) >= 2 && Number(age) <= 11).length,
              infants: ageMinors.filter(age => Number(age) < 2).length,
            })
          : undefined,
    };
  }
}

/**
 * Convierte un string the lowerCamelCase a snake_case
 * @param str
 */
function toSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/**
 * Convierte las keys de un objeto de lowerCamelCase a snake_case recursivamente
 * @param obj
 */
export function convertKeysToSnakeCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(item => convertKeysToSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const snakeKey = toSnakeCase(key);
        acc[snakeKey] = convertKeysToSnakeCase(obj[key]);
        return acc;
      },
      {} as { [key: string]: any },
    );
  }
  return obj;
}

export function getImageUrl(image: ImageTypeDto, width?: number, height?: number): string {
  const apiKey = process.env.GOOGLE_PLACES_API_KEY;

  switch (image.type) {
    case 'GOOGLE_ID':
      return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${width}&maxheight=${height}&photoreference=${image.image}&key=${apiKey}`;
    case 'URL':
      return image.image;
    case 'ID':
      const small = typeof height !== 'undefined' && typeof width !== 'undefined';
      return `https://media.staticontent.com/media/pictures/${image.image}${small ? `/${width}x${height}?op=TRUNCATE&enlarge=false` : ''}`;
  }
}

export function getVoiceMode(): SofiaVoiceMode {
  const voiceModeParam = getQueryParam('voiceMode');

  switch (voiceModeParam) {
    case 'MODAL':
    case 'DASHBOARD':
      return voiceModeParam;
    default:
      return 'DISABLED';
  }
}
